import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useChartColors } from '../../hooks/useDarkMode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function DeficitChart({ metrics }) {
  const colors = useChartColors();

  // Filter metrics with deficit data and sort by date
  const deficitData = metrics
    .filter(m => m.calorie_deficit !== null && m.calorie_deficit !== undefined)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const labels = deficitData.map(m => {
    const date = new Date(m.date);
    const label = `${date.getDate()}.${date.getMonth() + 1}`;
    // Mark excluded days with asterisk
    return m.exclude_from_trends ? `${label}*` : label;
  });

  const deficits = deficitData.map(m => m.calorie_deficit);

  // Calculate average (only non-excluded days)
  const includedDeficits = deficitData
    .filter(m => !m.exclude_from_trends)
    .map(m => m.calorie_deficit);
  const avgDeficit = includedDeficits.length > 0
    ? includedDeficits.reduce((sum, d) => sum + d, 0) / includedDeficits.length
    : 0;

  // Count excluded days for info display
  const excludedCount = deficitData.filter(m => m.exclude_from_trends).length;

  // Get colors based on theme
  const successColor = colors.success.border;
  const errorColor = colors.error.border;

  const data = {
    labels,
    datasets: [
      {
        label: 'Kaloriendefizit',
        data: deficits,
        backgroundColor: deficitData.map(m => {
          const d = m.calorie_deficit;
          // Excluded days get reduced opacity
          if (m.exclude_from_trends) {
            return d > 0
              ? (colors.isDark ? 'rgba(74, 222, 128, 0.25)' : 'rgba(34, 197, 94, 0.25)')
              : (colors.isDark ? 'rgba(248, 113, 113, 0.25)' : 'rgba(239, 68, 68, 0.25)');
          }
          return d > 0
            ? (colors.isDark ? 'rgba(74, 222, 128, 0.7)' : 'rgba(34, 197, 94, 0.7)')
            : (colors.isDark ? 'rgba(248, 113, 113, 0.7)' : 'rgba(239, 68, 68, 0.7)');
        }),
        borderColor: deficitData.map(m => {
          const d = m.calorie_deficit;
          if (m.exclude_from_trends) {
            return d > 0
              ? (colors.isDark ? 'rgba(74, 222, 128, 0.5)' : 'rgba(34, 197, 94, 0.5)')
              : (colors.isDark ? 'rgba(248, 113, 113, 0.5)' : 'rgba(239, 68, 68, 0.5)');
          }
          return d > 0 ? successColor : errorColor;
        }),
        borderWidth: deficitData.map(m => m.exclude_from_trends ? 2 : 1),
        borderRadius: 4,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: colors.tooltip.background,
        titleColor: colors.tooltip.text,
        bodyColor: colors.tooltip.text,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            const metric = deficitData[context.dataIndex];
            const label = `Defizit: ${value} kcal`;
            return metric?.exclude_from_trends ? `${label} (ausgeschlossen)` : label;
          }
        }
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Defizit (kcal)',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: (context) => {
            if (context.tick.value === 0) {
              return colors.gridZeroLine;
            }
            return colors.gridColor;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Datum',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: colors.gridColor,
        }
      }
    }
  };

  if (deficitData.length === 0) {
    return <p>Keine Defizit-Daten verfügbar</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '300px' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Bar data={data} options={options} />
      </div>
      <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)', flexShrink: 0 }}>
        Durchschnitt: {Math.round(avgDeficit)} kcal/Tag
        {excludedCount > 0 && (
          <span style={{ marginLeft: '1rem', opacity: 0.7 }}>
            ({excludedCount} ausgeschlossen*)
          </span>
        )}
      </p>
    </div>
  );
}

export default DeficitChart;
